import React from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { ReactComponent as GenieLogo } from "@/common/assets/vectors/genie_logo.svg";
import { AccessRequiredTooltip } from "@/common/components/AccessDeniedTooltip";
import { COLOR } from "@/common/enums/theme";
import {
  MaterialSymbol,
  SYMBOL_SIZES,
} from "@/ui/components/MaterialSymbol/MaterialSymbol";

export type AskGenieButtonComponentRenderer = (props: {
  size: "xsmall" | "small" | "medium";
  theme: "light" | "dark";
  isDisabled: boolean;
  isLoading: boolean;
  svgGenieLogo: React.ReactElement;
  GenieIconComponent: React.ReactElement;
}) => React.ReactNode;

/**
 * The Genie button, as drawn: an icon by default, or the caller's own look
 * (`variant`). Whether the user may ask is the caller's (`hasAccess`).
 */
export const AskGenieButtonView = ({
  onClick,
  disabled = false,
  loading = false,
  openOnboardingTooltip = false,
  onCloseOnboardingTooltip,
  size = "medium",
  theme: userTheme = "light",
  variant = "icon",
  dataTestId,
  svgViewBox: userProvidedSVGViewBox,
  hasAccess,
}: {
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  openOnboardingTooltip?: boolean;
  onCloseOnboardingTooltip?: () => void;
  size?: "xsmall" | "small" | "medium";
  theme?: "light" | "dark";
  variant?: "icon" | AskGenieButtonComponentRenderer;
  dataTestId?: string;
  svgViewBox?: string;
  hasAccess: boolean;
}) => {
  const theme = useTheme();
  const hasAskGeniePermission = hasAccess;

  const variantSx =
    userTheme === "light"
      ? {
          backgroundColor: theme.palette.common.white,
          "&:hover": { backgroundColor: theme.palette.grey[200] },
          color: theme.palette.grey[200],
        }
      : {
          backgroundColor: COLOR.NAVIGATION,
          color: theme.palette.common.white,
          "&:hover": { backgroundColor: theme.palette.grey[800] },
        };

  const sizeSx =
    size === "xsmall"
      ? {
          width: 26,
          height: 26,
          margin: 0.5,
        }
      : size === "small"
        ? {
            width: 34,
            height: 34,
          }
        : {
            width: 40,
            height: 40,
          };

  const viewBox =
    userProvidedSVGViewBox ||
    (size === "xsmall"
      ? "-6 -6 36 36"
      : size === "small"
        ? "-2 -2 28 28"
        : "0 0 24 24");

  const isDisabled = !hasAskGeniePermission || disabled || loading;
  const GenieIconComponent = (
    <Box>
      <SvgIcon
        component={GenieLogo}
        sx={{
          width: SYMBOL_SIZES.MEDIUM,
          height: SYMBOL_SIZES.MEDIUM,
          color:
            userTheme === "light"
              ? COLOR.NAVIGATION
              : theme.palette.common.white,
        }}
        viewBox={viewBox}
      />
      {loading && (
        <CircularProgress
          size={size === "xsmall" ? SYMBOL_SIZES.SMALL : SYMBOL_SIZES.MEDIUM}
          sx={{
            position: "absolute",
            color: "white",
            ...(size === "xsmall" ? { top: 6, left: 16 } : { top: 1, left: 1 }),
          }}
          thickness={6}
        />
      )}
    </Box>
  );

  return (
    <AccessRequiredTooltip hasAccess={hasAskGeniePermission} placement="bottom">
      <span>
        <Tooltip
          title={
            <Stack>
              <Stack
                direction={"row"}
                width="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" fontWeight={400}>
                  👋 Hey!
                </Typography>
                <IconButton
                  onClick={() => {
                    onCloseOnboardingTooltip && onCloseOnboardingTooltip();
                  }}
                  size="small"
                  sx={{
                    p: 0,
                  }}
                >
                  <MaterialSymbol
                    symbol="close"
                    size={SYMBOL_SIZES.XXSMALL}
                    color={"secondary.main"}
                  />
                </IconButton>
              </Stack>
              <Typography variant="body2" fontWeight={400}>
                Explore with Super Genie-your new command center! Click here to
                start.
              </Typography>
            </Stack>
          }
          placement="bottom"
          arrow
          open={openOnboardingTooltip}
        >
          {variant === "icon" ? (
            <IconButton
              data-testid={dataTestId}
              size={size === "xsmall" ? "small" : "medium"}
              sx={{
                ...variantSx,
                "&.Mui-disabled": {
                  backgroundColor: "text.disabled",
                },
                ...sizeSx,
              }}
              onClick={onClick}
              disabled={isDisabled}
            >
              {GenieIconComponent}
            </IconButton>
          ) : (
            <>
              {variant({
                size,
                theme: userTheme,
                isDisabled,
                isLoading: loading,
                svgGenieLogo: GenieLogo,
                GenieIconComponent,
              })}
            </>
          )}
        </Tooltip>
      </span>
    </AccessRequiredTooltip>
  );
};
